import calendar, { IDayCell } from './calendar'
import settings from './settings'
import occasions from './occasions'
import customOccasions from './custom-occasions'
import countryOccasions from './country-occasions'
import overrides from './occasion-overrides'
import events from './events'
import tasks from './tasks'
import notes from './notes'
import { occurrencesInRange } from './event-repeat'
import { UNTITLED, eventTime } from './phone-events'
import { Source } from './day-holidays'
import { parseQuery, IScope } from './search-query'
import { words, stem, rank, latestFirst, nearest, IRankable } from './search-text'
import vectors from './search-vectors'
import { OCCASION_ALIASES } from '../configs/occasion-aliases'

// Everything the app knows, as rows a query can be run over: Iran's
// occasions and the added countries' as the reader has left them, the
// reader's own occasions, events, tasks and notes. Each row knows its day, so
// a result can say when as well as what, and where a tap should go.
//
// An occasion has no date of its own, only a rule, so its rows are found by
// walking the days and asking each one the question the day page asks — the
// same way reminders are worked out, so a hijri day a moon-sighting correction
// has moved is found where the calendar shows it. Without a window the walk
// covers the year ahead, which holds the next occurrence of every yearly
// occasion; a window in the query is walked instead.

export type ResultKind = 'occasion' | 'holiday' | 'country' | 'mine' | 'event' | 'task' | 'note'

export interface ISearchRow extends IRankable {
    kind: ResultKind
    title: string
    day: IDayCell & { wd: number }
    time?: string // an event's hour
    country?: string // whose day, for a country's occasion
    done?: boolean // a task ticked off
    isHoliday: boolean
    source: Source // whose holiday colour it takes
    route: string
}

const DAY = 24 * 60 * 60 * 1000
const MAX_ROWS = 50
const MAX_WALK = 800 // days; a named year is 366 and the default window is the same

// Occasions first, then what the reader has on, then what is left to do, then
// what was written — when nothing else tells two rows apart.
const ORDER: Record<ResultKind, number> = {
    occasion: 0, holiday: 0, country: 0, mine: 0, event: 1, task: 2, note: 3,
}

// What the reader wrote, as against what the calendar ships. Word vectors
// search the reader's writing whatever the query spells elsewhere — see
// rank() in search-text.
const OWN: Record<ResultKind, boolean> = {
    occasion: false, holiday: false, country: false, mine: true, event: true, task: true, note: true,
}

const TAB: Record<ResultKind, string> = {
    occasion: 'agenda', holiday: 'agenda', country: 'agenda', mine: 'agenda',
    event: 'agenda', task: 'agenda', note: 'notes',
}

const local = (gy: number, gm: number, gd: number) => new Date(gy, gm - 1, gd)
const todayDate = () => {
    const t = calendar.today()
    return local(t.gy, t.gm, t.gd)
}
const cellOf = (d: Date) => calendar.dayAt(d.getFullYear(), d.getMonth() + 1, d.getDate(), 0)
const dateOf = (c: { gy: number, gm: number, gd: number }) => local(c.gy, c.gm, c.gd)

interface IExtra {
    time?: string
    country?: string
    done?: boolean
    isHoliday?: boolean
    source?: Source
}

function row(kind: ResultKind, title: string, text: string, day: IDayCell & { wd: number }, extra: IExtra = {}): ISearchRow {
    const terms = words(text)
    return {
        kind, title, day, terms,
        words: terms.map(stem),
        own: OWN[kind],
        distance: Math.round((dateOf(day).getTime() - todayDate().getTime()) / DAY),
        order: ORDER[kind],
        time: extra.time,
        country: extra.country,
        done: extra.done,
        isHoliday: extra.isHoliday ?? false,
        source: extra.source ?? 'iran',
        route: `/day?d=${day.gy}-${day.gm}-${day.gd}&t=${TAB[kind]}`,
    }
}

// --- occasions ---

// Rows by window, kept until a store changes. Walking a year costs a few
// hundred cheap conversions, which is nothing once but not something to do
// on every keystroke.
const cache = new Map<string, ISearchRow[]>()

function occasionRows(from: Date, to: Date): ISearchRow[] {
    const key = `${from.getTime()}-${to.getTime()}`
    const hit = cache.get(key)
    if (hit) return hit

    const out: ISearchRow[] = []
    for (let i = 0; i < MAX_WALK; i++) {
        const cell = calendar.dayAt(from.getFullYear(), from.getMonth() + 1, from.getDate(), i)
        if (dateOf(cell).getTime() >= to.getTime()) break

        // Both lists as the reader has left them: their names, and without the
        // ones they put away. See services/occasion-overrides.
        for (const o of overrides.builtIns(occasions.forDay(cell)).shown) {
            // Its other names too, so a search in the official calendar's
            // words finds it. See configs/occasion-aliases.
            const aliases = OCCASION_ALIASES[`${o.from}:${o.month}:${o.day}`] ?? []
            out.push(row(o.isHoliday ? 'holiday' : 'occasion', o.title,
                [o.title, o.original, ...aliases].join(' '), cell, { isHoliday: o.isHoliday }))
        }
        for (const o of overrides.countries(countryOccasions.forDay(cell)).shown) {
            out.push(row('country', o.title, [o.title, o.original, o.names.en, o.names.local].join(' '), cell,
                { country: o.countryName, isHoliday: o.type === 'public_holiday', source: 'abroad' }))
        }
        for (const o of customOccasions.forDay(cell)) {
            out.push(row('mine', o.title, o.title, cell))
        }
    }
    cache.set(key, out)
    return out
}

// A one-off of the reader's own outside the window: the walk cannot reach it,
// and it is still theirs to find — a wedding two years back as much as one
// two years on.
function oneOffsOutside(from: Date, to: Date): ISearchRow[] {
    const out: ISearchRow[] = []
    for (const o of customOccasions.all()) {
        if (o.repeat !== 'none') continue
        const at = dateOf(o).getTime()
        if (at >= from.getTime() && at < to.getTime()) continue
        out.push(row('mine', o.title, o.title, cellOf(dateOf(o))))
    }
    return out
}

// --- the reader's own ---

const inside = (c: { gy: number, gm: number, gd: number }, scope?: IScope) => {
    if (!scope) return true
    const at = dateOf(c).getTime()
    return at >= scope.from.getTime() && at < scope.to.getTime()
}

// Without a window an event is its next occurrence — or its first day, once
// the series has run out — so a weekly meeting is one row and not fifty.
// Inside a window every occurrence counts, because the window is the question.
function eventRows(scope?: IScope): ISearchRow[] {
    const today = todayDate()
    const from = scope ? scope.from : today
    const to = scope ? scope.to : new Date(today.getFullYear() + 1, today.getMonth(), today.getDate() + 1)
    const out: ISearchRow[] = []
    for (const e of events.all()) {
        if (e.kind === 'occasion') continue
        let occs = occurrencesInRange(e, from.getTime(), to.getTime())
        if (!scope) occs = occs.length ? occs.slice(0, 1) : [{ start: e.start, end: e.end }]
        for (const occ of occs.slice(0, 20)) {
            const cell = cellOf(new Date(occ.start))
            out.push(row('event', e.title.trim() || UNTITLED, [e.title, e.location, e.notes].join(' '), cell, {
                time: eventTime({ id: e.id, title: e.title, allDay: e.allDay, start: occ.start, end: occ.end, calendar: '' }),
            }))
        }
    }
    return out
}

const taskRows = (scope?: IScope) => tasks.all()
    .filter(t => inside(t, scope))
    .map(t => row('task', t.title, t.title, cellOf(dateOf(t)), { done: t.done }))

const noteRows = (scope?: IScope) => notes.all()
    .filter(n => inside(n, scope))
    .map(n => row('note', n.text, n.text, cellOf(dateOf(n))))

// --- the search ---

// The rows that answer the text, best first — or in date order when the text
// only names a kind or a window and there is nothing to be better at.
function search(text: string): ISearchRow[] {
    const q = parseQuery(text, calendar.today(), settings.get().system)
    if (!q.words.length && !q.kind && !q.holidays && !q.scope) return []

    const today = todayDate()
    const from = q.scope ? q.scope.from : today
    const to = q.scope ? q.scope.to : new Date(today.getFullYear(), today.getMonth(), today.getDate() + 366)
    const wants = (kind: 'occasion' | 'event' | 'task' | 'note') =>
        q.holidays ? kind === 'occasion'
            : !q.kind || q.kind === kind || (q.kind === 'happening' && (kind === 'occasion' || kind === 'event'))

    let rows: ISearchRow[] = []
    if (wants('occasion')) rows.push(...occasionRows(from, to), ...(q.scope ? [] : oneOffsOutside(from, to)))
    if (wants('event')) rows.push(...eventRows(q.scope))
    if (wants('task')) rows.push(...taskRows(q.scope))
    if (wants('note')) rows.push(...noteRows(q.scope))
    if (q.holidays) rows = rows.filter(r => r.isHoliday)

    const sorted = q.words.length
        ? rank(q.words, rows, vectors.similar(q.terms)).map(r => r.item)
        : q.scope ? latestFirst(rows) : nearest(rows)
    return sorted.slice(0, MAX_ROWS)
}

// A store has changed, or the day has turned: the walked rows are stale.
const invalidate = () => cache.clear()

export default { search, invalidate }
